import React from 'react';
import styled from 'styled-components';

import colors from '../../../../styles/colors';

// Z1 to Z5.
const zoneColors = [
  colors.shades.gamma9,
  colors.shades.info3,
  colors.shades.primary1,
  colors.shades.warning3,
  colors.base.danger,
];

const HeartSvg = (props) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <svg viewBox="0 0 24 24" fill={colors.base.danger} aria-hidden="true" {...props}>
    <path d="M12 21s-7-4.5-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6C19 16.5 12 21 12 21z" />
  </svg>
);

const HeartIcon = styled(HeartSvg)`
  width: ${({ $size = 11 }) => $size}px;
  height: ${({ $size = 11 }) => $size}px;
  flex-shrink: 0;
`;

/* Summary card */

const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 15px 20px;
  margin-bottom: 16px;
  border-radius: 5px;
  background-color: ${colors.base.alpha};
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

const SummaryTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  line-height: 26px;
  font-weight: 500;
  color: ${colors.shades.secondary3};
`;

const SyncInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${colors.shades.gamma10};
`;

const SyncChip = styled.span`
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 600;
  background-color: ${colors.shades.secondary5};
  color: ${colors.shades.secondary2};
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
`;

const KpiTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid ${colors.shades.gamma1};
  border-radius: 5px;
`;

const KpiLabel = styled.span`
  font-size: 12px;
  color: ${colors.shades.gamma10};
`;

const KpiValue = styled.span`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.shades.secondary3};
`;

const KpiUnit = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${colors.shades.gamma10};
`;

const KpiNote = styled.span`
  font-size: 12px;
  color: ${colors.shades.gamma10};
`;

const ChartsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`;

const ChartBlock = styled.div`
  flex: 1 1 320px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 13px;
  font-weight: 600;
  color: ${colors.shades.secondary3};
`;

const ChartUnit = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${colors.shades.gamma10};
`;

const ChartTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const HelpButton = styled.button`
  width: 16px;
  height: 16px;
  padding: 0;
  border: 1px solid ${colors.shades.gamma10};
  border-radius: 50%;
  background: transparent;
  color: ${colors.shades.gamma10};
  font-family: inherit;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  cursor: help;
`;

const ZoneHelp = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 4px 2px;
  font-size: 12px;
  line-height: 16px;
`;

const ZoneHelpRow = styled.div`
  display: grid;
  grid-template-columns: 10px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
`;

const ZoneHelpSwatch = styled.span`
  width: 10px;
  height: 10px;
  margin-top: 3px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

const DayBars = styled.div`
  height: 140px;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
  align-items: end;
  border-bottom: 1px solid ${colors.shades.gamma8};
`;

const DayBarColumn = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const DayBarValue = styled.span`
  font-size: 11px;
  white-space: nowrap;
  font-weight: ${({ $muted }) => ($muted ? 400 : 600)};
  color: ${({ $muted }) => ($muted ? colors.shades.gamma7 : colors.shades.secondary3)};
`;

const DayBar = styled.div`
  width: 100%;
  max-width: 44px;
  height: ${({ $height }) => $height};
  border-radius: 3px 3px 0 0;
  background-color: ${({ $isPeak }) => ($isPeak ? colors.shades.secondary2 : colors.shades.primary1)};
`;

const EmptyDayBar = styled.div`
  width: 100%;
  max-width: 44px;
  height: 4px;
  background-color: ${colors.shades.gamma8};
`;

const DayLabels = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 10px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  color: ${colors.shades.gamma10};
`;

const StackedBar = styled.div`
  display: flex;
  height: 28px;
  gap: 2px;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${colors.shades.gamma5};
`;

const StackedSegment = styled.div`
  width: ${({ $share }) => $share}%;
  background-color: ${({ $color }) => $color};
`;

const ZoneLegend = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px 16px;
  font-size: 12px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
  background-color: ${({ $color }) => $color};
`;

const Strong = styled.span`
  font-weight: 600;
`;

const Muted = styled.span`
  color: ${colors.shades.gamma10};
`;

/* Per-workout stats and details */

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 12px;
  margin: 2px 0 4px 22px;
  font-size: 11px;
  line-height: 16px;
  color: ${colors.shades.beta1};
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const DetailsToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 2px;
  border: 0;
  background: transparent;
  font-family: inherit;
  font-size: 11px;
  font-weight: 500;
  color: ${colors.shades.secondary2};
  cursor: pointer;
  &::after {
    content: '${({ $isExpanded }) => ($isExpanded ? ' ▲' : ' ▼')}';
    font-size: 8px;
  }
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px 12px;
  border-top: 1px solid ${colors.addAlpha(colors.shades.gamma5, 0.5)};
`;

const DetailsTitle = styled.span`
  font-size: 11px;
  font-weight: 600;
  line-height: 20px;
  color: ${colors.base.beta};
`;

const DetailsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const DetailsCard = styled.div`
  flex: ${({ $grow = 1 }) => $grow} 1 ${({ $basis = '260px' }) => $basis};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 5px;
  background-color: ${colors.base.alpha};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: ${colors.shades.secondary3};
`;

const ChartSvg = styled.svg`
  display: block;
  width: 100%;
  height: auto;
`;

const ZoneRow = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 48px;
  gap: 8px;
  align-items: center;
  font-size: 12px;
`;

const ZoneTrack = styled.div`
  height: 8px;
  border-radius: 4px;
  background-color: ${colors.shades.gamma4};
`;

const ZoneFill = styled.div`
  width: ${({ $share }) => $share}%;
  height: 8px;
  border-radius: 4px;
  background-color: ${({ $color }) => $color};
`;

const ZoneTime = styled.span`
  text-align: right;
`;

const CaloriesRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
  padding-top: 10px;
  border-top: 1px solid ${colors.shades.gamma1};
  font-size: 12px;
`;

export {
  zoneColors,
  ChartTitle,
  HelpButton,
  ZoneHelp,
  ZoneHelpRow,
  ZoneHelpSwatch,
  HeartIcon,
  SummaryCard,
  SummaryHeader,
  SummaryTitle,
  SyncInfo,
  SyncChip,
  KpiGrid,
  KpiTile,
  KpiLabel,
  KpiValue,
  KpiUnit,
  KpiNote,
  ChartsRow,
  ChartBlock,
  ChartHeader,
  ChartUnit,
  DayBars,
  DayBarColumn,
  DayBarValue,
  DayBar,
  EmptyDayBar,
  DayLabels,
  StackedBar,
  StackedSegment,
  ZoneLegend,
  LegendItem,
  Swatch,
  Strong,
  Muted,
  StatsRow,
  StatItem,
  DetailsToggle,
  DetailsSection,
  DetailsTitle,
  DetailsRow,
  DetailsCard,
  CardHeader,
  ChartSvg,
  ZoneRow,
  ZoneTrack,
  ZoneFill,
  ZoneTime,
  CaloriesRow,
};
